import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import { fetchAllegati } from "../../../graphql/controllers/fetchAllegati";
import { buildAiResultsWhere } from "./aiResultTypes";

const PAGE_SIZE = 20;

const extractConnection = (response) => response?.data?.allegatis || null;

export const mergeUniqueItems = (currentItems, nextItems) => {
  const itemsById = new Map();
  [...currentItems, ...nextItems].forEach((item) => {
    if (item?.objectId) {
      itemsById.set(item.objectId, item);
    }
  });
  return Array.from(itemsById.values());
};

const useGiottoAiResults = ({
  typeSlugs = [],
  orgId = null,
  enabled = true,
}) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedQueryKey, setResolvedQueryKey] = useState(null);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const skipRef = useRef(0);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);
  const typeKey = typeSlugs.join(",");
  const queryKey = `${isProd ? "prod" : "dev"}|${orgId || "all"}|${typeKey || "all"}|${enabled}`;

  const where = useMemo(
    () => buildAiResultsWhere({ typeSlugs, orgId }),
    // typeKey intentionally represents the stable, ordered filter identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeKey, orgId]
  );

  const fetchPage = useCallback(async ({ append = false } = {}) => {
    if (!enabled || (append && inFlightRef.current)) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    inFlightRef.current = true;
    setError(null);

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setResolvedQueryKey(null);
    }

    try {
      const token = await getTokenSilently();
      const env = isProd ? "prod" : "dev";
      const response = await fetchAllegati(
        {
          url: `${config.baseUrl[env]}/graphql`,
          token,
        },
        {
          where,
          skip: append ? skipRef.current : 0,
          first: PAGE_SIZE,
        }
      );

      if (requestId !== requestIdRef.current) return;

      const connection = extractConnection(response);
      if (!connection) {
        throw new Error("Risposta GraphQL non valida");
      }

      const nextItems = (connection.edges || [])
        .map((edge) => edge?.node)
        .filter(Boolean);
      const totalCount = connection.count || 0;
      skipRef.current = append
        ? skipRef.current + nextItems.length
        : nextItems.length;

      setItems((currentItems) =>
        append ? mergeUniqueItems(currentItems, nextItems) : nextItems
      );
      setCount(totalCount);
      setHasMore(
        connection.pageInfo?.hasNextPage ?? skipRef.current < totalCount
      );
      setInitialFetchDone(true);
      setResolvedQueryKey(queryKey);
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching Giotto AI results:", fetchError);
      setError(fetchError);
      setInitialFetchDone(true);
      setResolvedQueryKey(queryKey);
      if (!append) {
        setItems([]);
        setCount(0);
        setHasMore(false);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [enabled, getTokenSilently, isProd, queryKey, where]);

  const refresh = useCallback(() => {
    requestIdRef.current += 1;
    inFlightRef.current = false;
    skipRef.current = 0;
    setItems([]);
    setCount(0);
    setHasMore(false);
    setInitialFetchDone(false);
    setResolvedQueryKey(null);
    return fetchPage({ append: false });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || inFlightRef.current) return;
    return fetchPage({ append: true });
  }, [fetchPage, hasMore, loading, loadingMore]);

  useEffect(() => {
    requestIdRef.current += 1;
    inFlightRef.current = false;
    skipRef.current = 0;
    setItems([]);
    setCount(0);
    setHasMore(false);
    setError(null);

    if (!enabled) {
      setLoading(false);
      setLoadingMore(false);
      setInitialFetchDone(true);
      setResolvedQueryKey(queryKey);
      return undefined;
    }

    setInitialFetchDone(false);
    fetchPage({ append: false });

    return () => {
      requestIdRef.current += 1;
      inFlightRef.current = false;
    };
  }, [enabled, fetchPage, queryKey]);

  return {
    items,
    count,
    hasMore,
    loading,
    loadingMore,
    initialFetchDone,
    isCurrentQuery: resolvedQueryKey === queryKey,
    error,
    loadMore,
    refresh,
  };
};

export default useGiottoAiResults;
